import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SelectField } from '@/components/app/DriftUI';
import { NbDateInput } from '@/components/app/DateTimeFields';
import { DEVIATION_KINDS, DEVIATION_KIND_LABELS, DEVIATION_STATUSES, DEVIATION_STATUS_LABELS, DRIFT_FILTER_LABELS, EVENT_STATUS_LABELS, EVENT_TYPE_LABELS, FAULT_STATUSES, PERIOD_PRESETS, PRIORITIES, PRIORITY_LABELS, SERVICE_EVENT_STATUSES, SEVERITIES, SEVERITY_LABELS, TIME_STATUS_LABELS, filterValueLabel, resolvePeriod } from '@/lib/driftApi';
import { osloDateOf, formatDateShort } from '@/domain/time';
import { cn } from '@/lib/utils';

/*
  Felles filter for driftssidene (Prosjekter, Timer, Vareforbruk, driftsrapporter).
  Alt ligger i URL-en, så KPI → detalj → tilbake beholder utvalget, og lenker kan deles.
  Primært: periode + prosjekt. Alt annet under «Flere filtre». Aktive valg vises som chips.
*/

export const PERIOD_KEYS = ['periode', 'from', 'to'];
/** Parametre som ikke er filtre, men styrer skjema/visning. Fjernes aldri av «Nullstill». */
const CONTROL_KEYS = new Set(['ny', 'rediger', 'groupBy', 'scenario']);

export function useDriftFilters({ keys = [], defaultPreset = '14' } = {}) {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const today = osloDateOf(new Date());
  const keyList = useMemo(() => keys, [keys]);

  const values = useMemo(() => {
    const v = {};
    for (const k of keyList) v[k] = params.get(k) || 'alle';
    return v;
  }, [params, keyList]);

  const period = useMemo(() => resolvePeriod({ periode: params.get('periode'), from: params.get('from'), to: params.get('to') }, today, defaultPreset), [params, today, defaultPreset]);
  const groupBy = params.get('groupBy') || undefined;

  const update = useCallback(
    (mutate) => {
      const next = new URLSearchParams(params);
      mutate(next);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const set = useCallback(
    (key, value) =>
      update((next) => {
        if (!value || value === 'alle') next.delete(key);
        else next.set(key, value);
        if (key === 'projectId') next.delete('subprojectId');
        if (key === 'category') next.delete('itemId');
        if (key === 'type') next.delete('eventStatus');
      }),
    [update],
  );

  const setPeriod = useCallback(
    (preset, from, to) =>
      update((next) => {
        if (!preset || preset === defaultPreset) next.delete('periode');
        else next.set('periode', preset);
        if (preset === 'egen') {
          if (from) next.set('from', from);
          else next.delete('from');
          if (to) next.set('to', to);
          else next.delete('to');
        } else {
          next.delete('from');
          next.delete('to');
        }
      }),
    [update, defaultPreset],
  );

  const setGroupBy = useCallback((g) => update((next) => (g ? next.set('groupBy', g) : next.delete('groupBy'))), [update]);

  /** Fjerner styreparametre (ny, rediger …) uten å røre filtrene. */
  const remove = useCallback((...removeKeys) => update((next) => removeKeys.forEach((k) => next.delete(k))), [update]);

  const reset = useCallback(
    () =>
      update((next) => {
        for (const k of [...keyList, ...PERIOD_KEYS]) next.delete(k);
      }),
    [update, keyList],
  );

  /** Parametre til API-kall (uten «alle»). */
  const query = useMemo(() => {
    const q = { from: period.from, to: period.to };
    for (const k of keyList) if (values[k] !== 'alle') q[k] = values[k];
    return q;
  }, [period, values, keyList]);

  /** Søkestreng som bærer periode + valgte filtre videre til en annen driftsside. */
  const carry = useCallback(
    (extra = {}, { only } = {}) => {
      const next = new URLSearchParams();
      for (const k of PERIOD_KEYS) if (params.get(k)) next.set(k, params.get(k));
      for (const k of keyList) {
        if (only && !only.includes(k)) continue;
        if (values[k] !== 'alle') next.set(k, values[k]);
      }
      Object.entries(extra).forEach(([k, v]) => {
        if (v === undefined || v === null || v === '' || v === 'alle') next.delete(k);
        else next.set(k, String(v));
      });
      const s = next.toString();
      return s ? `?${s}` : '';
    },
    [params, values, keyList],
  );

  const activeCount = keyList.filter((k) => values[k] !== 'alle').length;

  return { params, values, period, groupBy, set, setPeriod, setGroupBy, remove, reset, query, carry, activeCount, search: location.search, today, controlKeys: CONTROL_KEYS };
}

const control = 'h-9 lg:h-9 text-[14px] lg:text-[14px]';

/** Periode: forhåndsvalg + egen fra/til. */
function PeriodControl({ f, testPrefix }) {
  const { period, setPeriod } = f;
  return (
    <>
      <SelectField value={period.preset} onValueChange={(v) => setPeriod(v, period.from, period.to)} options={PERIOD_PRESETS.map((p) => ({ value: p.id, label: p.label }))} className={cn(control, 'w-full sm:w-[168px]')} testId={`${testPrefix}-filter-period`} />
      {period.preset === 'egen' && (
        <div className="flex w-full items-center gap-1.5 sm:w-auto" data-testid={`${testPrefix}-filter-custom-period`}>
          <NbDateInput value={period.from ?? ''} max={period.to} onChange={(v) => setPeriod('egen', v, period.to)} ariaLabel="Fra dato" size="compact" showQuick={false} className="min-w-0 flex-1 sm:w-[150px] sm:flex-none" testId={`${testPrefix}-filter-from`} />
          <span className="text-[13px] text-[var(--hr-muted-2)]">–</span>
          <NbDateInput value={period.to ?? ''} min={period.from} onChange={(v) => setPeriod('egen', period.from, v)} ariaLabel="Til dato" size="compact" showQuick={false} className="min-w-0 flex-1 sm:w-[150px] sm:flex-none" testId={`${testPrefix}-filter-to`} />
        </div>
      )}
    </>
  );
}

function MoreField({ label, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-[13px] font-medium text-[var(--hr-muted)]">{label}</Label>
      {children}
    </div>
  );
}

/** Valg for ett sekundærfilter. */
function moreOptions(key, { refs, machines, values }) {
  const all = (label) => ({ value: 'alle', label });
  switch (key) {
    case 'subprojectId': {
      const p = refs?.projects?.find((x) => x.id === values.projectId);
      return { options: [all('Alle underprosjekter'), ...(p?.subprojects ?? []).map((s) => ({ value: s.id, label: s.name }))], disabled: !p || !p.subprojects?.length, hint: !p ? 'Velg prosjekt først' : !p.subprojects?.length ? 'Prosjektet har ingen underprosjekter' : null };
    }
    case 'employeeId':
      return { options: [all('Alle medarbeidere'), ...(refs?.employees ?? []).map((e) => ({ value: e.id, label: e.name }))] };
    case 'machineId':
      return { options: [all('Alle'), { value: 'med', label: 'Kun med maskin' }, { value: 'uten', label: 'Uten maskin' }, ...(machines ?? []).map((m) => ({ value: m.machineId, label: `${m.internalNo} · ${m.name}` }))] };
    case 'status':
      return { options: [all('Alle statuser'), ...Object.entries(TIME_STATUS_LABELS).map(([value, label]) => ({ value, label }))] };
    case 'activityId':
      return { options: [all('Alle aktiviteter'), ...(refs?.activities ?? []).map((a) => ({ value: a.id, label: a.label }))] };
    case 'wageTypeId':
      return { options: [all('Alle lønnsarter'), ...(refs?.wageTypes ?? []).map((w) => ({ value: w.id, label: w.label }))] };
    case 'category':
      return { options: [all('Alle kategorier'), ...(refs?.categories ?? []).map((c) => ({ value: c.id, label: c.label }))] };
    case 'itemId': {
      const items = (refs?.items ?? []).filter((i) => values.category === 'alle' || i.category === values.category);
      return { options: [all('Alle varer'), ...items.map((i) => ({ value: i.id, label: i.name, hint: refs?.units?.[i.unit] ?? i.unit }))] };
    }
    case 'type':
      return { options: [all('Alle typer'), ...Object.entries(EVENT_TYPE_LABELS).map(([value, label]) => ({ value, label }))] };
    case 'eventStatus': {
      const statuses = values.type === 'feilmelding' ? FAULT_STATUSES : values.type === 'service' || values.type === 'vedlikehold' ? SERVICE_EVENT_STATUSES : [...FAULT_STATUSES, ...SERVICE_EVENT_STATUSES];
      return { options: [all('Alle statuser'), ...statuses.map((value) => ({ value, label: EVENT_STATUS_LABELS[value] }))] };
    }
    case 'priority':
      return { options: [all('Alle prioriteter'), ...PRIORITIES.map((value) => ({ value, label: PRIORITY_LABELS[value] }))], hint: 'Gjelder feilmeldinger' };
    case 'assignedToId':
      return { options: [all('Alle'), ...(refs?.employees ?? []).map((e) => ({ value: e.id, label: e.name, hint: e.role }))] };
    case 'open':
      return { options: [all('Åpne og avsluttede'), { value: '1', label: 'Kun åpne saker' }, { value: '0', label: 'Kun avsluttede saker' }] };
    case 'kind':
      return { options: [all('RUH og kvalitet'), ...DEVIATION_KINDS.map((value) => ({ value, label: DEVIATION_KIND_LABELS[value] }))] };
    case 'devStatus':
      return { options: [all('Alle statuser'), ...DEVIATION_STATUSES.map((value) => ({ value, label: DEVIATION_STATUS_LABELS[value] }))] };
    case 'severity':
      return { options: [all('Alle alvorlighetsgrader'), ...SEVERITIES.map((value) => ({ value, label: SEVERITY_LABELS[value] }))] };
    case 'responsibleId':
      return { options: [all('Alle'), ...(refs?.employees ?? []).map((e) => ({ value: e.id, label: e.name, hint: e.role }))] };
    default:
      return { options: [all('Alle')] };
  }
}

/**
 * Filterrad for driftssidene. Egen rad (ikke i PanelHeader), så hjelpetekst og kontroller aldri klemmes.
 * `more` er nøklene som ligger i «Flere filtre». `children` plasseres til høyre (f.eks. gruppering).
 */
export function DriftFilterBar({ f, refs, machines = [], more = [], showProject = true, children, className, testPrefix = 'drift' }) {
  const [open, setOpen] = useState(false);
  const moreActive = more.filter((k) => f.values[k] && f.values[k] !== 'alle').length;
  const chips = Object.keys(f.values)
    .filter((k) => f.values[k] !== 'alle')
    .map((k) => ({ key: k, label: DRIFT_FILTER_LABELS[k] ?? k, value: filterValueLabel(k, f.values[k], refs, machines) }));
  const customPeriod = f.period.preset === 'egen' && (f.period.from || f.period.to);

  return (
    <div className={cn('flex flex-col gap-2.5 border-b border-[var(--hr-hairline)] px-4 py-3 sm:px-5', className)} data-testid={`${testPrefix}-filters`}>
      <div className="flex flex-wrap items-center gap-2">
        <PeriodControl f={f} testPrefix={testPrefix} />
        {showProject && (
          <SelectField value={f.values.projectId ?? 'alle'} onValueChange={(v) => f.set('projectId', v)} options={[{ value: 'alle', label: 'Alle prosjekter' }, ...(refs?.projects ?? []).map((p) => ({ value: p.id, label: p.name }))]} className={cn(control, 'w-full sm:w-[220px]')} testId={`${testPrefix}-filter-project`} />
        )}
        {more.length > 0 && (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className={cn(control, 'w-full justify-between gap-2 rounded-[10px] border-[var(--hr-border)] bg-white px-3 font-medium text-[var(--hr-text)] shadow-none hover:border-[rgba(31,33,48,0.28)] hover:bg-white data-[state=open]:border-[var(--hr-focus)] sm:w-auto')} data-testid={`${testPrefix}-filter-more`} aria-label="Flere filtre">
                <span className="inline-flex items-center gap-2">
                  <SlidersHorizontal className="h-3.5 w-3.5 text-[var(--hr-muted-2)]" aria-hidden="true" />
                  Flere filtre
                </span>
                {moreActive > 0 && <span className="hr-tabular inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[var(--hr-heading)] px-1.5 text-[12px] font-semibold text-white" data-testid={`${testPrefix}-filter-more-count`}>{moreActive}</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" sideOffset={6} collisionPadding={12} className="flex w-[320px] max-w-[calc(100vw-24px)] max-h-[min(var(--radix-popover-content-available-height),calc(100vh-24px))] flex-col overflow-hidden rounded-[12px] p-0 shadow-[var(--hr-shadow-md)]" data-testid={`${testPrefix}-filter-more-popover`}>
              <div className="hr-scroll min-h-0 flex-1 overflow-y-auto p-4">
                <div className="grid gap-3">
                  {more.map((k) => {
                    const o = moreOptions(k, { refs, machines, values: f.values });
                    return (
                      <MoreField key={k} label={DRIFT_FILTER_LABELS[k] ?? k}>
                        <SelectField value={f.values[k] ?? 'alle'} onValueChange={(v) => f.set(k, v)} options={o.options} disabled={o.disabled} className={control} testId={`${testPrefix}-filter-${k}`} />
                        {o.hint && <p className="text-[13px] text-[var(--hr-muted-2)]">{o.hint}</p>}
                      </MoreField>
                    );
                  })}
                </div>
              </div>
              <div className="flex shrink-0 items-center justify-between gap-2 border-t border-[var(--hr-hairline)] bg-white px-4 py-3">
                <Button variant="ghost" size="sm" className="h-8 rounded-[8px] px-2 text-[13px] text-[var(--hr-muted)]" onClick={() => more.forEach((k) => f.set(k, 'alle'))} disabled={!moreActive} data-testid={`${testPrefix}-filter-more-clear`}>
                  Tøm disse
                </Button>
                <Button size="sm" className="h-8 rounded-[8px] bg-[var(--hr-heading)] px-3 text-[13px] text-white hover:bg-[var(--hr-navy)]" onClick={() => setOpen(false)} data-testid={`${testPrefix}-filter-more-close`}>
                  Ferdig
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        )}
        {children && <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">{children}</div>}
      </div>

      {(chips.length > 0 || customPeriod) && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid={`${testPrefix}-active-filters`}>
          <span className="text-[13px] text-[var(--hr-muted-2)]">Aktive filtre:</span>
          {customPeriod && (
            <span className="inline-flex h-7 items-center rounded-full border border-[var(--hr-hairline)] bg-white px-2.5 text-[13px] font-medium text-[var(--hr-text)]" data-testid={`${testPrefix}-chip-period`}>
              <span className="mr-1 text-[var(--hr-muted)]">Periode:</span>
              {f.period.from ? formatDateShort(f.period.from) : '…'} – {f.period.to ? formatDateShort(f.period.to) : '…'}
            </span>
          )}
          {chips.map((c) => (
            <span key={c.key} className="inline-flex h-7 items-center gap-0.5 rounded-full border border-[var(--hr-hairline)] bg-white py-0 pl-2.5 pr-0.5 text-[13px] font-medium text-[var(--hr-text)]" data-testid={`${testPrefix}-chip-${c.key}`}>
              <span className="mr-1 text-[var(--hr-muted)]">{c.label}:</span>
              <span className="max-w-[220px] truncate">{c.value}</span>
              <button type="button" onClick={() => f.set(c.key, 'alle')} aria-label={`Fjern filter: ${c.label}`} className="flex h-6 w-6 items-center justify-center rounded-full text-[var(--hr-muted)] transition-colors duration-150 hover:bg-[var(--hr-inset)] hover:text-[var(--hr-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" data-testid={`${testPrefix}-chip-${c.key}-remove`}>
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </span>
          ))}
          <Button variant="ghost" size="sm" className="h-7 rounded-full px-2.5 text-[13px] text-[var(--hr-muted)] hover:text-[var(--hr-heading)]" onClick={f.reset} data-testid={`${testPrefix}-filters-reset`}>
            Nullstill
          </Button>
        </div>
      )}
    </div>
  );
}
